import { Measures } from "../../../telemetry/Measures";
import { CriticalData } from "../../../telemetry/CriticalData";
import { Trace } from "../../../telemetry/Trace";
import { dashboard } from "../../../telemetry/TraceLogging";
import { clearLaunchAttributionContext, setLaunchAttributionContext } from "../../externalApi/DashboardHostApi";
import { SetStaticWidgetPinnedStatus } from "../../externalApi/WidgetPickerApi";
import { tryParseJson } from "../../utils/stringUtilities";
import { getWidgetOrchestrator } from "../orchestrator/orchestrator";
import * as Constants from "../../constants";
// This implementation handles signals from WebXT, parses them, and emits telemetry
// Privacy is not a concern here, as our own telemetry already handles user settings for privacy
export class WidgetSignalApi {
    constructor() {
        this.getTelemetryWidgetInfo = (widget) => ({
            appId: widget.appId,
            boardView: widget.boardView,
            isPeregrine: widget.isPeregrine,
            isPinned: widget.isPinned,
            providerId: widget.providerId,
            widgetDefinitionId: widget.widgetDefinitionId,
            widgetInstanceId: widget.widgetId,
            widgetState: widget.widgetState,
            widgetStateReason: widget.widgetStateReason,
        });
        this.initLoadingSignals();
        this._feedLoadedSuccessfully = false;
        this._widgetList = new Array();
    }
    getWebxtWidgetListForTelemetry() {
        return this._widgetList.map((instance) => this.getTelemetryWidgetInfo(instance));
    }
    sendSignal(signal) {
        Trace.logEvent(dashboard, "SendSignal", 1 /* information */, {
            name: signal.name,
            payload: signal.payload,
            signalType: signal.signalType,
        });
        switch (signal.name) {
            case "OnWidgetClick":
                this.onWidgetClick(signal);
                break;
            case "OnSettingsInteracted":
                this.onSettingsInteracted(signal);
                break;
            case "PageControlsInteracted":
                this.onPageControlsInteracted(signal);
                break;
            case "OnFeedUriClicked":
                this.onFeedUriClicked(signal);
                break;
            case "OnFeedSettingsUriClicked":
                this.onFeedSettingsUriClicked(signal);
                break;
            case "WidgetListOnAppLaunch":
                this.widgetListOnAppLaunch(signal);
                break;
            case "WidgetStateUpdated":
                this.widgetStateUpdated(signal);
                break;
            case "WidgetLayoutUpdateCompleted":
                this.widgetLayoutUpdateCompleted(signal);
                break;
            case "OnBoardNavigateAway":
                this.onBoardNavigateAway(signal);
                break;
            case "StartPersonalizationPageInteracted":
                this.startPersonalizationPageInteracted(signal);
                break;
            case "BoardsInteracted":
                this.boardsInteracted(signal);
                break;
            case "WebAppPerfLog":
                this.webAppPerfLog(signal);
                break;
            default:
                Measures.logEvent(dashboard, "UnexpectedSignal", 16777216 /* productAndServicePerformance */, {
                    name: signal.name,
                    payload: signal.payload,
                    signalType: signal.signalType,
                });
                break;
        }
    }
    onWidgetClick(signal) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        // Set context for host app Uri launch handling in case this leads to a Url launch.
        // Except a few special cases, we have no accurate way of knowing which click interactions will lead to a Uri launch.
        // This might not always work as the Url launch is asynchronous.
        setLaunchAttributionContext(signal.payload);
        let widgetActionPayload = tryParseJson(signal.payload);
        CriticalData.logEvent(dashboard, "OnWidgetClick", 33554432 /* productAndServiceUsage */, {
            actionType: (_a = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.clickAction) === null || _a === void 0 ? void 0 : _a.actionType,
            appId: (_b = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.widgetItem) === null || _b === void 0 ? void 0 : _b.appId,
            boardView: (_c = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.widgetItem) === null || _c === void 0 ? void 0 : _c.boardView,
            isPeregrine: (_d = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.widgetItem) === null || _d === void 0 ? void 0 : _d.isPeregrine,
            isPlatform: (_e = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.widgetItem) === null || _e === void 0 ? void 0 : _e.isPlatform,
            launchSource: (_f = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.clickAction) === null || _f === void 0 ? void 0 : _f.clickType,
            providerId: (_g = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.widgetItem) === null || _g === void 0 ? void 0 : _g.providerId,
            widgetDefinitionId: (_h = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.widgetItem) === null || _h === void 0 ? void 0 : _h.widgetDefinitionId,
            widgetInstanceId: (_j = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.widgetItem) === null || _j === void 0 ? void 0 : _j.widgetId,
        });
    }
    onSettingsInteracted(signal) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        clearLaunchAttributionContext();
        let widgetActionPayload = tryParseJson(signal.payload);
        Measures.logEvent(dashboard, "OnSettingsInteracted", 33554432 /* productAndServiceUsage */, {
            appId: (_a = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.widgetItem) === null || _a === void 0 ? void 0 : _a.appId,
            boardView: (_b = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.widgetItem) === null || _b === void 0 ? void 0 : _b.boardView,
            change: (_c = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.settingsAction) === null || _c === void 0 ? void 0 : _c.settingsType,
            isPeregrine: (_d = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.widgetItem) === null || _d === void 0 ? void 0 : _d.isPeregrine,
            isPinned: (_e = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.widgetItem) === null || _e === void 0 ? void 0 : _e.isPinned,
            isPlatform: (_f = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.widgetItem) === null || _f === void 0 ? void 0 : _f.isPlatform,
            newSize: (_g = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.settingsAction) === null || _g === void 0 ? void 0 : _g.newSize,
            oldSize: (_h = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.settingsAction) === null || _h === void 0 ? void 0 : _h.oldSize,
            providerId: (_j = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.widgetItem) === null || _j === void 0 ? void 0 : _j.providerId,
            state: (_k = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.widgetItem) === null || _k === void 0 ? void 0 : _k.widgetState,
            widgetInstanceId: (_l = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.widgetItem) === null || _l === void 0 ? void 0 : _l.widgetId,
            widgetDefinitionId: (_m = widgetActionPayload === null || widgetActionPayload === void 0 ? void 0 : widgetActionPayload.widgetItem) === null || _m === void 0 ? void 0 : _m.widgetDefinitionId,
        });
    }
    onPageControlsInteracted(signal) {
        clearLaunchAttributionContext();
        let pageControlsInteractedPayload = tryParseJson(signal.payload);
        if (pageControlsInteractedPayload) {
            let pageInteractionType = pageControlsInteractedPayload.pageInteractionType;
            Measures.logEvent(dashboard, "PageControlsInteracted", 16777216 /* productAndServicePerformance */, {
                boardView: pageControlsInteractedPayload.boardView,
                interactionType: pageInteractionType,
            });
        }
    }
    onFeedUriClicked(signal) {
        var _a, _b, _c, _d, _e;
        // Set context for host app Uri launch handling in case this leads to a Url launch.
        // Except a few special cases, we have no accurate way of knowing which click interactions will lead to a Uri launch.
        // This might not always work as the Url launch is asynchronous.
        setLaunchAttributionContext(signal.payload);
        let feedActionPayload = tryParseJson(signal.payload);
        Measures.logEvent(dashboard, "FeedUriClicked", 33554432 /* productAndServiceUsage */, {
            appId: (_a = feedActionPayload === null || feedActionPayload === void 0 ? void 0 : feedActionPayload.feedItem) === null || _a === void 0 ? void 0 : _a.appId,
            isPeregrine: (_b = feedActionPayload === null || feedActionPayload === void 0 ? void 0 : feedActionPayload.feedItem) === null || _b === void 0 ? void 0 : _b.isPeregrine,
            isPlatform: (_c = feedActionPayload === null || feedActionPayload === void 0 ? void 0 : feedActionPayload.feedItem) === null || _c === void 0 ? void 0 : _c.isPlatform,
            launchSource: (_d = feedActionPayload === null || feedActionPayload === void 0 ? void 0 : feedActionPayload.clickAction) === null || _d === void 0 ? void 0 : _d.clickType,
            providerId: (_e = feedActionPayload === null || feedActionPayload === void 0 ? void 0 : feedActionPayload.feedItem) === null || _e === void 0 ? void 0 : _e.providerId,
        });
    }
    onFeedSettingsUriClicked(signal) {
        var _a, _b, _c, _d, _e;
        // Set context for host app Uri launch handling in case this leads to a Url launch.
        // Except a few special cases, we have no accurate way of knowing which click interactions will lead to a Uri launch.
        // This might not always work as the Url launch is asynchronous.
        setLaunchAttributionContext(signal.payload);
        let feedActionPayload = tryParseJson(signal.payload);
        Measures.logEvent(dashboard, "FeedSettingsUriClicked", 33554432 /* productAndServiceUsage */, {
            appId: (_a = feedActionPayload === null || feedActionPayload === void 0 ? void 0 : feedActionPayload.feedItem) === null || _a === void 0 ? void 0 : _a.appId,
            isPeregrine: (_b = feedActionPayload === null || feedActionPayload === void 0 ? void 0 : feedActionPayload.feedItem) === null || _b === void 0 ? void 0 : _b.isPeregrine,
            isPlatform: (_c = feedActionPayload === null || feedActionPayload === void 0 ? void 0 : feedActionPayload.feedItem) === null || _c === void 0 ? void 0 : _c.isPlatform,
            launchSource: (_d = feedActionPayload === null || feedActionPayload === void 0 ? void 0 : feedActionPayload.clickAction) === null || _d === void 0 ? void 0 : _d.clickType,
            providerId: (_e = feedActionPayload === null || feedActionPayload === void 0 ? void 0 : feedActionPayload.feedItem) === null || _e === void 0 ? void 0 : _e.providerId,
        });
    }
    widgetListOnAppLaunch(signal) {
        // TODO: https://task.ms/48700520
        let widgetListPayload = tryParseJson(signal.payload);
        if (widgetListPayload === null || widgetListPayload === void 0 ? void 0 : widgetListPayload.widgetItems) {
            // reset the signals for for AppContentLoadProgress
            this.initLoadingSignals();
            this._peregrineContentExpected = widgetListPayload.widgetItems
                .map((widget) => widget.isPeregrine)
                .filter((boolean) => boolean === true).length;
            this._pinnedContentExpected = widgetListPayload.widgetItems
                .map((widget) => widget.isPinned)
                .filter((boolean) => boolean === true).length;
            this._thirdPartyContentExpected = widgetListPayload.widgetItems
                .map((widget) => widget.isPlatform)
                .filter((boolean) => boolean === true).length;
            this._widgetContentExpected = widgetListPayload.widgetItems.length;
            this._allContentExpected += this._widgetContentExpected;
            // The state comes in as blank; set to loading to detect content updates later
            this._widgetList = widgetListPayload.widgetItems.map((widget) => {
                widget.widgetState = widget.isPeregrine ? "Loading" : "Unknown";
                return widget;
            });
            Measures.logEvent(dashboard, "WidgetListOnAppLaunch", 16777216 /* productAndServicePerformance */, {
                allContentExpected: this._allContentExpected,
                peregrineContentExpected: this._peregrineContentExpected,
                thirdPartyContentExpected: this._thirdPartyContentExpected,
                pinnedContentExpected: this._pinnedContentExpected,
                widgetContentExpected: this._widgetContentExpected,
                widgetList: this._widgetList.map((instance) => this.getTelemetryWidgetInfo(instance)),
            });
            // Check if pinned widgets match
            this.checkPinnedWidgetList();
        }
    }
    widgetStateUpdated(signal) {
        // On initial load, this signal is sent for peregrine widgets (state change from loading -> normal|error) by WebXT
        // and for non-peregrine widgets by the web app itself (state change from unknown -> loading -> normal|error)
        // For non-peregrine widgets, this is also sent whenever widget state changes outside of initial load. Eg. pinning, reload.
        let widgetStatePayload = tryParseJson(signal.payload);
        if (widgetStatePayload) {
            let updatedWidget = widgetStatePayload.widgetItem;
            let currentWidget = null;
            let currentUpdatedCount = this._allContentError + this._allContentReady;
            // We send the signal for feed from webapp code; needs to be handled differently from other signals from webxt
            if ((signal.signalType === "WDX" && updatedWidget.widgetDefinitionId === "com.msn.desktopfeed") ||
                updatedWidget.widgetId === "com.msn.desktopfeed") {
                if (updatedWidget.widgetState === "Normal") {
                    this._feedLoadedSuccessfully = true;
                }
                else if (updatedWidget.widgetState === "Error") {
                    this._feedLoadedSuccessfully = false;
                }
                Measures.logEvent(dashboard, "WidgetStateUpdated", 16777216 /* productAndServicePerformance */, {
                    updatedWidget: updatedWidget,
                });
                // feed does not contribute to load progress
                return;
            }
            else {
                if (!updatedWidget.isPeregrine && signal.signalType !== "WDX") {
                    // Feed signals non-peregrine widgets as "normal" when they have been told to load
                    // Ignore the WebXT signal for non-peregrine widgets
                    return;
                }
                this._widgetList = this._widgetList.map((widget) => {
                    // Initial widget state will be "loading" for peregrine widgets and "unknown" for non-peregrine widgets
                    if (widget.widgetInstanceId === updatedWidget.widgetInstanceId &&
                        widget.widgetDefinitionId === updatedWidget.widgetDefinitionId &&
                        (widget.widgetState === "Loading" || widget.widgetState === "Unknown")) {
                        if (signal.signalType === "WDX") {
                            // preserve the pinned state for non-peregrine
                            updatedWidget.isPinned = widget.isPinned;
                        }
                        currentWidget = widget;
                        if (updatedWidget.widgetState === "Normal") {
                            this._allContentReady++;
                            this._widgetContentReady++;
                            widget.isPeregrine ? this._peregrineContentReady++ : 0;
                            widget.isPinned ? this._pinnedContentReady++ : 0;
                            widget.isPlatform ? this._thirdPartyContentReady++ : 0;
                        }
                        else if (updatedWidget.widgetState === "Error") {
                            this._allContentError++;
                            this._widgetContentError++;
                            widget.isPeregrine ? this._peregrineContentError++ : 0;
                            widget.isPinned ? this._pinnedContentError++ : 0;
                            widget.isPlatform ? this._thirdPartyContentError++ : 0;
                        }
                        return updatedWidget;
                    }
                    else {
                        return widget;
                    }
                });
            }
            // If currentWidget is null, the signal is not for initial load
            // In this case, we only log updated widget information
            Measures.logEvent(dashboard, "WidgetStateUpdated", 16777216 /* productAndServicePerformance */, {
                currentWidget: currentWidget !== null && currentWidget !== void 0 ? currentWidget : undefined,
                updatedWidget: updatedWidget,
            });
            // Try send static/peregrine widget pinned status to host app/xaml picker on cold launch.
            // Handling calendar & todo as special case (these are not peregrine but not platform either)
            if (currentWidget &&
                (updatedWidget.isPeregrine ||
                    updatedWidget.widgetDefinitionId === Constants.CalendarWidgetId ||
                    updatedWidget.widgetDefinitionId === Constants.ToDoWidgetId) &&
                updatedWidget.isPinned) {
                SetStaticWidgetPinnedStatus(updatedWidget.widgetDefinitionId, /*pinned*/ true);
            }
            if (currentUpdatedCount < this._allContentReady + this._allContentError) {
                Measures.logEvent(dashboard, "AppContentLoadProgress", 16777216 /* productAndServicePerformance */, {
                    feedLoadedSuccessfully: this._feedLoadedSuccessfully,
                    allContentExpected: this._allContentExpected,
                    allContentReady: this._allContentReady,
                    allContentError: this._allContentError,
                    allContentPercentLoaded: ((this._allContentReady + this._allContentError) / this._allContentExpected).toString(),
                    isReauthRequiredUserEnabled: true,
                    isSignedOutUserEnabled: true,
                    widgetContentExpected: this._widgetContentExpected,
                    widgetContentReady: this._widgetContentReady,
                    widgetContentError: this._widgetContentError,
                    widgetContentPercentLoaded: ((this._widgetContentReady + this._widgetContentError) /
                        this._widgetContentExpected).toString(),
                    peregrineContentExpected: this._peregrineContentExpected,
                    peregrineContentReady: this._peregrineContentReady,
                    peregrineContentError: this._peregrineContentError,
                    peregrineContentPercentLoaded: ((this._peregrineContentReady + this._peregrineContentError) /
                        this._peregrineContentExpected).toString(),
                    thirdPartyContentExpected: this._thirdPartyContentExpected,
                    thirdPartyContentReady: this._thirdPartyContentReady,
                    thirdPartyContentError: this._thirdPartyContentError,
                    thirdPartyContentPercentLoaded: ((this._thirdPartyContentReady + this._thirdPartyContentError) /
                        this._thirdPartyContentExpected).toString(),
                    pinnedContentExpected: this._pinnedContentExpected,
                    pinnedContentReady: this._pinnedContentReady,
                    pinnedContentError: this._pinnedContentError,
                    pinnedContentPercentLoaded: ((this._pinnedContentReady + this._pinnedContentError) /
                        this._pinnedContentExpected).toString(),
                });
            }
        }
    }
    widgetLayoutUpdateCompleted(signal) {
        var _a;
        let widgetLayoutUpdatedPayload = tryParseJson(signal.payload);
        if (widgetLayoutUpdatedPayload) {
            Measures.logEvent(dashboard, "WidgetLayoutUpdateCompleted", 16777216 /* productAndServicePerformance */, {
                trigger: widgetLayoutUpdatedPayload.trigger,
                wasSuccessful: widgetLayoutUpdatedPayload.layoutUpdateSucceeded,
                widgetList: ((_a = widgetLayoutUpdatedPayload.widgetList) === null || _a === void 0 ? void 0 : _a.widgetItems)
                    ? widgetLayoutUpdatedPayload.widgetList.widgetItems.map((instance) => this.getTelemetryWidgetInfo(instance))
                    : "",
            });
        }
    }
    onBoardNavigateAway(signal) {
        clearLaunchAttributionContext();
        let boardActionPayload = tryParseJson(signal.payload);
        if (boardActionPayload) {
            Measures.logEvent(dashboard, "OnBoardNavigateAway", 16777216 /* productAndServicePerformance */, {
                boardView: boardActionPayload.boardView,
                durationInMiliseconds: boardActionPayload.durationInMiliseconds,
                durationInMilisecondsFromRender: boardActionPayload.durationInMilisecondsFromRender,
                hiddenReason: boardActionPayload.hiddenReason,
                prevBoardView: boardActionPayload.prevBoardView,
            });
        }
    }
    startPersonalizationPageInteracted(signal) {
        clearLaunchAttributionContext();
        let startPageInteractionTypePayload = tryParseJson(signal.payload);
        if (startPageInteractionTypePayload) {
            Measures.logEvent(dashboard, "StartPersonalizationPageInteracted", 16777216 /* productAndServicePerformance */, {
                contentId: startPageInteractionTypePayload === null || startPageInteractionTypePayload === void 0 ? void 0 : startPageInteractionTypePayload.contentId,
                pageInteractionType: startPageInteractionTypePayload === null || startPageInteractionTypePayload === void 0 ? void 0 : startPageInteractionTypePayload.pageInteractionType,
                source: startPageInteractionTypePayload === null || startPageInteractionTypePayload === void 0 ? void 0 : startPageInteractionTypePayload.source,
            });
        }
    }
    boardsInteracted(signal) {
        clearLaunchAttributionContext();
        let boardActionPayload = tryParseJson(signal.payload);
        if (boardActionPayload) {
            Measures.logEvent(dashboard, "BoardsInteracted", 16777216 /* productAndServicePerformance */, {
                boardView: boardActionPayload.boardView,
                durationInMiliseconds: boardActionPayload.durationInMiliseconds,
                durationInMilisecondsFromRender: boardActionPayload.durationInMilisecondsFromRender,
                interactionType: boardActionPayload.source
            });
        }
    }
    webAppPerfLog(signal) {
        clearLaunchAttributionContext();
        let webAppPerfLogPayload = tryParseJson(signal.payload);
        if (webAppPerfLogPayload) {
            Measures.logEvent(dashboard, "WebAppPerfLog", 16777216 /* productAndServicePerformance */, {
                payload: webAppPerfLogPayload
            });
        }
    }
    async checkPinnedWidgetList() {
        const orch = await getWidgetOrchestrator();
        const pinnedWidgetsList = (await orch.getWidgetInstances()).map((widget) => ({
            widgetId: widget.widgetId,
            widgetInstanceId: widget.widgetInstanceId,
            widgetDefinitionId: widget.widgetDefinitionId,
        }));
        const webXTPinnedWidgetsList = this._widgetList
            .filter((widget) => widget.isPinned === true)
            .map((widget) => ({
            widgetId: String(widget.widgetId),
            widgetDefinitionId: String(widget.widgetDefinitionId),
            widgetInstanceId: String(widget.widgetInstanceId),
        }));
        const additionalWidgets = webXTPinnedWidgetsList.filter((webxtPinnedWidget) => !pinnedWidgetsList.some(function (pinnedWidget) {
            return webxtPinnedWidget.isPeregrine
                ? webxtPinnedWidget.widgetDefinitionId === pinnedWidget.widgetDefinitionId
                : webxtPinnedWidget.widgetDefinitionId === pinnedWidget.widgetDefinitionId &&
                    webxtPinnedWidget.widgetInstanceId === pinnedWidget.widgetInstanceId;
        }));
        const missingWidgets = pinnedWidgetsList.filter((pinnedWidget) => !webXTPinnedWidgetsList.some(function (webxtPinnedWidget) {
            return pinnedWidget.isPeregrine
                ? webxtPinnedWidget.widgetDefinitionId === pinnedWidget.widgetDefinitionId
                : webxtPinnedWidget.widgetDefinitionId === pinnedWidget.widgetDefinitionId &&
                    webxtPinnedWidget.widgetInstanceId === pinnedWidget.widgetInstanceId;
        }));
        Measures.logEvent(dashboard, "PinnedWidgetPersistenceCheck", 16777216 /* productAndServicePerformance */, {
            additionalWidgets: additionalWidgets,
            checkPassed: pinnedWidgetsList.length === webXTPinnedWidgetsList.length &&
                additionalWidgets.length === 0 &&
                missingWidgets.length === 0,
            numPinnedWidgets: pinnedWidgetsList.length,
            numWebxtPinnedWidgets: webXTPinnedWidgetsList.length,
            missingWidgets: missingWidgets,
        });
    }
    initLoadingSignals() {
        this._allContentExpected = 0;
        this._allContentReady = 0;
        this._allContentError = 0;
        this._widgetContentExpected = 0;
        this._widgetContentReady = 0;
        this._widgetContentError = 0;
        this._peregrineContentExpected = 0;
        this._peregrineContentReady = 0;
        this._peregrineContentError = 0;
        this._thirdPartyContentExpected = 0;
        this._thirdPartyContentReady = 0;
        this._thirdPartyContentError = 0;
        this._pinnedContentExpected = 0;
        this._pinnedContentReady = 0;
        this._pinnedContentError = 0;
    }
}
